import React from 'react';
import { produce } from 'immer';
// A result that holds data is handed to the map function as complete, even when Apollo reports it incomplete.
const hasCachedResult = ({ complete, previousData, }) => complete || (typeof previousData === 'object' && previousData !== null && Object.keys(previousData).length > 0);
/**
 * Wrapper for the (resilient) query hook, which replaces the native updateQuery function with
 * immer, so we can use immutable data without all the spreading of the current cache values.
 *
 * The current, mutable cache value gets wrapped in the immer.produce function and the mapping
 * function normally passed to the `updateQuery` function gets forwarded the wrapped,
 * now immutable datastructure.
 *
 * This allows changing values in the mapping function by directly assigning values to the
 * `cache` object properties instead of having to spread the (possibly deep nested) cache objects with the
 * sole purpose of creating new objects (aka. new shallow), so apollo notices the new values
 * by its shallow comparision
 *
 * An update arriving while the cache holds no result for the query — usually a mutation finishing before the
 * query has returned, which happens mostly in e2e tests or on slow connections — is held back and applied once
 * the query's data arrives. Held updates are applied in order, so they can go stale if several pile up, which is
 * unlikely enough to accept. A result Apollo reports as incomplete but that holds data is updated straight away:
 * some queries render their data while the cache diff stays incomplete, and holding their updates would lose them.
 *
 * @param queryResult return value of the `useResilientQuery`
 * @param skip the `skip` option the query ran with, which decides `called`
 * @returns the same queryResult, just that we can now use immutable data in the `updateQuery`.
 * @example
 *
 * ```
 * const {updateQuery} = useImmerQueryWrapper(useResilientQuery(SomeQuery, options), {skip: options?.skip});
 * ```
 *
 * @example
 *
 * this is without the immer wrapper:
 *
 * ```ts
 * const {updateQuery} = useResilientQuery(SomeQuery);
 *
 * subscribeToMutationResult(mutationResult => {
 *   updateQuery((_, {complete, previousData}) => {
 *     if (!complete) {
 *       return;
 *     }
 *     return {
 *       ...previousData,
 *       deep: {
 *         ...previousData.deep,
 *         nested: {
 *           ...previousData.deep.nested,
 *           object: {
 *             ...mutationResult.object
 *           }
 *         }
 *       }
 *     }
 *   })
 * })
 * ```
 *
 * and this is the same update with the immer wrapper:
 *
 * ```ts
 * const {updateQuery} = useImmerQueryWrapper(useResilientQuery(SomeQuery, options), {skip: options?.skip});
 *
 * subscribeToMutationResult(mutationResult => {
 *   updateQuery(immerCache => {
 *     immerCache.deep.nested.object = mutationResult.object;
 *     return immerCache;
 *   })
 * })
 * ```
 */
export function useImmerQueryWrapper(queryResult, { skip }) {
    const { data, updateQuery } = queryResult;
    const heldUpdates = React.useRef([]);
    React.useEffect(() => {
        if (heldUpdates.current.length === 0 || !data) {
            return;
        }
        updateQuery((_, options) => {
            if (!hasCachedResult(options)) {
                return;
            }
            const updates = heldUpdates.current;
            heldUpdates.current = [];
            return updates.reduce((result, mapFn) => produce(result, draft => {
                mapFn(draft, { ...options, complete: true, previousData: result });
            }), options.previousData);
        });
    }, [data, updateQuery]);
    const immerUpdateQuery = (mapFn) => {
        updateQuery((_, options) => {
            if (!hasCachedResult(options)) {
                heldUpdates.current.push(mapFn);
                return;
            }
            return produce(options.previousData, draft => {
                mapFn(draft, options);
            });
        });
    };
    return { ...queryResult, updateQuery: immerUpdateQuery, called: !skip };
}
